
import { observer } from "mobx-react-lite";
import { useState, useCallback } from "react";
import { useSearchParams } from "@/lib/router-compat";
import { useRouter } from "@/lib/router-compat";
import Link from "@/components/ui/link-compat";
import { CheckSquare, User, UserCheck, Pencil, FolderKanban, AlignLeft, CircleDot } from "lucide-react";
import { useDeal, useDeleteDeal } from "@/hooks/useDeals";
import { useDealStore, useUserStore, useDealTypeStore, useContactStore, useProjectStore, useTaskStore } from "@/stores/StoreProvider";
import { ContactType, CrmTaskStatus } from "@/types/crm";
import { RelatedTable } from "@/components/ui/related-table";
import { LinkedRecordCard } from "@/components/ui/linked-record-card";
import { Checkbox } from "@/components/ui/checkbox";
import { DealCustomFieldsCard } from "@/components/deals/deal-custom-fields";
import { InlineField } from "@/components/ui/inline-field";
import { InlineSelectField } from "@/components/ui/inline-select-field";
import { usePageTitle } from '@/hooks/usePageTitle';
import { useDeferredMount } from '@/hooks/useDeferredMount';
import { useEntityTimeline } from "@/hooks/useTimeline";
import { useUpdateNote, useDeleteNote } from "@/hooks/useNotes";
import { useUploadAttachments, useDeleteAttachment } from "@/hooks/useNoteAttachments";
import { useAuth } from "@/contexts/AuthContext";
import { ActivityTimeline } from "@/components/ui/activity-timeline";
import { useTasks } from "@/hooks/useTasks";
import { CrmTaskPriority, DealStatus, type CrmTask, type CustomFieldDefinition } from "@/types/crm";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { lazy, Suspense } from "react";
import { DealDetailHeader } from "@/components/deals/deal-detail-header";
const NoteComposer = lazy(() => import("@/components/notes/note-composer").then(m => ({ default: m.NoteComposer })));
const DealDialog = lazy(() => import("@/components/deals/deal-dialog").then(m => ({ default: m.DealDialog })));
const TaskDialog = lazy(() => import("@/components/tasks/task-dialog").then(m => ({ default: m.TaskDialog })));
import { DetailPageSkeleton } from "@/components/ui/skeletons";
import { DeleteConfirmation } from "@/components/ui/delete-confirmation";

const DealDetailPage = observer(function DealDetailPage() {
  const bodyReady = useDeferredMount();
  const searchParams = useSearchParams();
  const id = searchParams.get("id") ?? "";
  const { data: deal, isLoading: dealLoading } = useDeal(id);
  const dealStore = useDealStore();
  const userStore = useUserStore();
  const dealTypeStore = useDealTypeStore();
  const contactStore = useContactStore();
  const projectStore = useProjectStore();
  const taskStore = useTaskStore();
  const saveField = (field: string) => (value: string) => {
    dealStore.update(id, { [field]: value || null });
  };
  const ownerOptions = userStore.all.map((u) => ({
    value: u.id,
    label: u.displayName || u.email,
  }));
  const contactOptions = (() => {
    const all = contactStore.all;
    const companies = all.filter((c) => c.type === ContactType.Company);
    const independentPersons = all.filter((c) => c.type === ContactType.Person && !c.companyId);
    const opts: { value: string; label: string }[] = [];
    for (const company of companies) {
      opts.push({ value: company.id, label: company.name });
      for (const emp of contactStore.getByCompanyId(company.id)) {
        opts.push({ value: emp.id, label: `  └ ${emp.name}` });
      }
    }
    for (const person of independentPersons) {
      opts.push({ value: person.id, label: person.name });
    }
    return opts;
  })();
  const projectOptions = projectStore.all.map((p) => ({
    value: p.id,
    label: p.name,
  }));
  const statusOptions = [
    { value: "0", label: "Open" },
    { value: "1", label: "Won" },
    { value: "2", label: "Lost" },
  ];
  usePageTitle(deal?.dealTypeName ?? 'Deal');

  const dealType = deal ? dealTypeStore.getById(deal.dealTypeId) : undefined;
  const customFieldDefs = (dealType?.customFieldsSchema as { fields?: CustomFieldDefinition[] } | undefined)?.fields ?? [];
  const userOptions = ownerOptions;
  const resolveUserName = useCallback(
    (uid: string) => userStore.getById(uid)?.displayName ?? uid,
    [userStore]
  );
  const saveCustomField = useCallback(
    (fieldName: string, value: unknown) => {
      if (!deal) return;
      const merged = { ...(deal.customFields ?? {}), [fieldName]: value };
      if (value === null || value === undefined) delete (merged as Record<string, unknown>)[fieldName];
      dealStore.update(id, { customFields: merged });
    },
    [deal, dealStore, id]
  );
  const { userId } = useAuth();
  const timeline = useEntityTimeline("deal", id);
  const timelineEntries = timeline.entries;
  const updateNote = useUpdateNote("deal", id);
  const deleteNote = useDeleteNote("deal", id);
  const uploadAttachments = useUploadAttachments("deal", id);
  const deleteAttachment = useDeleteAttachment("deal", id);
  const { data: tasks = [] } = useTasks({ dealId: id });
  const deleteDeal = useDeleteDeal();
  const router = useRouter();
  const [isTaskDialogOpen, setIsTaskDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const linkedProject = deal?.projectId ? projectStore.getById(deal.projectId) : undefined;
  const linkedContact = deal?.contactId ? contactStore.getById(deal.contactId) : undefined;

  if (!bodyReady || dealLoading) {
    return <DetailPageSkeleton />;
  }

  if (!deal) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-muted-foreground">Deal not found.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <DealDetailHeader deal={deal} onDelete={() => setIsDeleteOpen(true)} />

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6">
        {/* Main content: notes */}
        {/* Mobile-only bottom inset: the shell's scroll-container padding is not
            honored by Chromium flex scrolling, so clear the fixed bottom nav here. */}
        <div className="space-y-4 min-w-0 order-2 lg:order-1 pb-[calc(var(--mobile-bottom-nav-height)+env(safe-area-inset-bottom))] lg:pb-0">
          <NoteComposer entityType="deal" entityId={id} />
          <ActivityTimeline
            entries={timelineEntries}
            isLoading={timeline.isLoading}
            hasMore={timeline.hasMore}
            onLoadMore={() => timeline.loadMore()}
            isLoadingMore={timeline.isLoadingMore}
            currentEntityId={id}
            onEditNote={(noteId, content) => updateNote.mutateAsync({ noteId, content })}
            onDeleteNote={(noteId) => deleteNote.mutateAsync(noteId)}
            onUploadFiles={(noteId, files) => uploadAttachments.mutateAsync({ noteId, files }).then(() => {})}
            onDeleteAttachment={(noteId, attachmentId) => deleteAttachment.mutate({ noteId, attachmentId })}
            currentUserId={userId ?? undefined}
          />
          <Button className="h-11 w-full lg:hidden" onClick={() => setIsEditDialogOpen(true)}>Edit deal</Button>
        </div>

        {/* Sidebar */}
        <aside className="space-y-4 order-1 lg:order-2 lg:sticky lg:top-20 lg:self-start">
          {/* Details card */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium">Details</CardTitle>
                <Button size="sm" variant="ghost" className="h-8 px-2" aria-label="Edit deal" onClick={() => setIsEditDialogOpen(true)}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <InlineSelectField
                label="Status"
                value={String(deal.status)}
                options={statusOptions}
                onSave={(val) => dealStore.update(id, { status: Number(val) as DealStatus })}
                icon={<CircleDot className="h-4 w-4" />}
              />
              <InlineSelectField
                label="Contact"
                value={deal.contactId ?? null}
                options={contactOptions}
                onSave={(val) => dealStore.update(id, { contactId: val || undefined })}
                icon={<User className="h-4 w-4" />}
                emptyText="No contact"
                placeholder="Select contact…"
                renderValue={(opt) =>
                  opt ? (
                    <Link
                      href={`/dashboard/contact?id=${opt.value}`}
                      className="text-sm hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {opt.label}
                    </Link>
                  ) : null
                }
              />
              <InlineSelectField
                label="Project"
                value={deal.projectId ?? null}
                options={projectOptions}
                onSave={(val) => dealStore.update(id, { projectId: val || null })}
                icon={<FolderKanban className="h-4 w-4" />}
                emptyText="No project"
                placeholder="Select project…"
                renderValue={(opt) =>
                  opt ? (
                    <Link
                      href={`/dashboard/project?id=${opt.value}`}
                      className="text-sm hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {opt.label}
                    </Link>
                  ) : null
                }
              />
              <InlineField
                label="Description"
                value={deal.description}
                onSave={saveField("description")}
                multiline
                icon={<AlignLeft className="h-4 w-4" />}
              />
              <InlineSelectField
                label="Owner"
                value={deal.ownerId ?? null}
                options={ownerOptions}
                onSave={(val) => dealStore.update(id, { ownerId: val || null })}
                icon={<UserCheck className="h-4 w-4" />}
                emptyText="No owner"
                placeholder="Select owner…"
              />
            </CardContent>
          </Card>

          {/* Custom fields card */}
          {customFieldDefs.length > 0 && deal && (
            <DealCustomFieldsCard
              dealTypeName={dealType?.name ?? "Custom"}
              fields={customFieldDefs}
              values={(deal.customFields ?? {}) as Record<string, unknown>}
              userOptions={userOptions}
              resolveUserName={resolveUserName}
              onSaveField={saveCustomField}
            />
          )}

          <RelatedTable
            icon={<CheckSquare className="h-4 w-4" />}
            title="Tasks"
            count={tasks.length}
            rows={tasks}
            getRowKey={(task) => task.id}
            onRowClick={(task) => router.push(`/dashboard/task?id=${task.id}`)}
            quickAdd={{ ariaLabel: "Add task", onClick: () => setIsTaskDialogOpen(true) }}
            emptyText="No tasks yet."
            columns={[
              {
                key: "done",
                render: (task: CrmTask) => (
                  <span onClick={(event) => event.stopPropagation()} className="inline-flex">
                    <Checkbox
                      checked={task.status === CrmTaskStatus.Completed}
                      aria-label={`Complete ${task.title}`}
                      onCheckedChange={() =>
                        taskStore.update(task.id, { status: CrmTaskStatus.Completed })
                      }
                    />
                  </span>
                ),
              },
              { key: "title", render: (task: CrmTask) => <span className="truncate">{task.title}</span> },
              {
                key: "priority",
                render: (task: CrmTask) => (
                  <Badge
                    variant={
                      task.priority === CrmTaskPriority.High
                        ? "destructive"
                        : task.priority === CrmTaskPriority.Medium
                          ? "default"
                          : "secondary"
                    }
                    className="text-xs"
                  >
                    {task.priority === CrmTaskPriority.High
                      ? "High"
                      : task.priority === CrmTaskPriority.Medium
                        ? "Med"
                        : "Low"}
                  </Badge>
                ),
              },
            ]}
          />

          {deal.projectId && (
            <LinkedRecordCard
              icon={<FolderKanban className="h-3.5 w-3.5" />}
              title="Linked project"
              name={deal.projectName || linkedProject?.name || "Project"}
              subtitle={
                linkedProject
                  ? `${linkedProject.dealsCount} deals · ${linkedProject.tasksCount} tasks`
                  : undefined
              }
              href={`/dashboard/project?id=${deal.projectId}`}
            />
          )}
          {deal.contactId && (
            <LinkedRecordCard
              icon={<User className="h-3.5 w-3.5" />}
              title="Linked contact"
              name={deal.contactName || linkedContact?.name || "Contact"}
              subtitle={
                linkedContact
                  ? linkedContact.email ??
                    (linkedContact.type === ContactType.Company ? "Company" : "Person")
                  : undefined
              }
              href={`/dashboard/contact?id=${deal.contactId}`}
            />
          )}
        </aside>
      </div>

      {isEditDialogOpen && <DealDialog
        deal={deal}
        open={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
      />}

      <DeleteConfirmation
        open={isDeleteOpen}
        onOpenChange={setIsDeleteOpen}
        onConfirm={() => { deleteDeal.mutateAsync({ id, name: deal.dealTypeName }); router.push("/dashboard/deals"); }}
        entityType="deal"
        entityName={deal.dealTypeName}
      />

      {isTaskDialogOpen && <TaskDialog
        task={null}
        open={isTaskDialogOpen}
        onClose={() => setIsTaskDialogOpen(false)}
        defaultDealId={id}
        defaultContactId={deal.contactId}
      />}
    </div>
  );
});


import { createFileRoute } from '@tanstack/react-router';
export const Route = createFileRoute('/dashboard/deal')({
  component: DealDetailPage,
  pendingComponent: () => <DetailPageSkeleton />,
  pendingMs: 0,
  pendingMinMs: 150,
});
